import { memo, useState, useRef, useEffect } from "react";

import { useTranslation } from 'react-i18next';
/**
 * LazyImage — Wrapper component for images with:
 * - Native lazy loading (loading="lazy")
 * - srcset support for responsive images
 * - IntersectionObserver-based fade-in
 * - Fallback placeholder while loading
 * - Dark theme skeleton loader
 */
const LazyImage = memo(function LazyImage({
  src,
  alt,
  className = "",
  srcSet,
  sizes,
  loading = "lazy",
  decoding = "async",
  width,
  height,
  style,
  imgRef: externalRef,
  ...rest
}) {
    const { t } = useTranslation();
const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const internalRef = useRef(null);
  const imgRef = externalRef || internalRef;

  useEffect(() => {
    if (!imgRef.current) return;
    const el = imgRef.current;

    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }

    let didCancel = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !didCancel) {
          setInView(true);
          observer.unobserve(el);
        }
      },
      { rootMargin: "400px" }
    );

    try {
      observer.observe(el);
    } catch {
      setInView(true);
    }

    // Safety timeout: if IO never fires within 3s, show image anyway
    const safetyTimer = setTimeout(() => {
      if (!didCancel) setInView(true);
    }, 3000);

    return () => {
      didCancel = true;
      clearTimeout(safetyTimer);
      observer.disconnect();
    };
  }, [imgRef]);

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={style}
    >
      {/* Skeleton placeholder */}
      {(!loaded || !inView) && (
        <div className="absolute inset-0 bg-zinc-800/60 animate-pulse" aria-hidden="true" />
      )}

      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading={loading}
        decoding={decoding}
        srcSet={srcSet}
        sizes={sizes}
        width={width}
        height={height}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={`${className} transition-opacity duration-500 ${loaded && inView ? 'opacity-100' : 'opacity-0'}`}
        style={undefined}
        {...rest}
      />
    </div>
  );
});

export default LazyImage;
